import styled from 'styled-components';

/**
 * Customs systems send a message as a single line, so it has to wrap rather than scroll the drawer
 * sideways. `pre` keeps the indentation `formatXmlMessage` adds, which a `div` would collapse.
 */
const Pre = styled.pre`
    white-space: pre-wrap;
    overflow-wrap: anywhere;
`;

/**
 * Puts every XML element on its own line and indents it by depth; an element holding a value keeps
 * it on the element's own line. Anything that does not look like XML - a JSON body, an already
 * formatted message - is returned unchanged, so nothing is ever hidden from the reader.
 */
export const formatXmlMessage = (rawMessage: string | undefined | null): string => {
    if (!rawMessage?.trim().startsWith('<')) return rawMessage ?? '';

    const lines = rawMessage.trim().replace(/>\s*</g, '>\n<').split('\n');

    let depth = 0;
    return lines
        .map((line) => {
            const isClosing = line.startsWith('</');
            // A declaration, a comment or a self-closing element opens nothing.
            const isStandalone = /^<[?!]/.test(line) || line.endsWith('/>');
            // `<a>text</a>` on one line both opens and closes, so it does not change the depth.
            const isSelfContained = /^<([^\s/>]+)[^>]*>.*<\/\1>$/.test(line);

            if (isClosing) depth = Math.max(0, depth - 1);

            const indented = `${'  '.repeat(depth)}${line}`;

            if (!isClosing && !isStandalone && !isSelfContained) depth += 1;

            return indented;
        })
        .join('\n');
};

/** The raw message customs sent, rendered so it can actually be read. */
const RawMessage = ({ children }: { children: string | undefined | null }) => <Pre>{formatXmlMessage(children)}</Pre>;

export default RawMessage;
